<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { usePlayerProfileStore } from '@/stores/player-profile';
import { GAMIFICATION_SLUGS } from '@/types/player-profile';
import { levelGateMessage } from '@/utils/apostatudo/crm-level-slug';
import { usePromocodeStore } from '@/stores/promocode';
import {
  PROMOCODE_TERMS,
  PROMOCODE_TERMS_INTRO,
  PROMOCODE_TERMS_TITLE,
} from '@/utils/apostatudo/promocode-terms';
import { getAssetsUrlForCDN } from '@/utils/template';

// Modal do Promocode. Mesma mecânica da roletinha e do Check-in Diário: Teleport
// pro body, slide-up por @keyframes (o reset global de transition em main.scss
// mata `transition`) e a store controla a abertura sem navegar de rota.
//
// A casca é a do BaseNoticeSheet (arte de cabeçalho sangrando por cima do painel
// + partículas), pra os dois estados desta feature — formulário e aviso de
// elegibilidade — serem a MESMA folha. As regras de uso abrem num popover
// ancorado no (i), igual ao regulamento do Check-in Diário.
const store = usePromocodeStore();
const { isOpen, status, prefillCode, isRedeeming } = storeToRefs(store);

// Cor de destaque: o laranja do botão de resgate, que é a cor da feature. Mesmo
// papel do azul da roletinha e do dourado do check-in — partículas do fundo e
// arte do aviso saem daqui.
const ACCENT_COLOR = '#FF704A';

// `visible` mantém o overlay montado até o slide-down terminar; `sheetOpen`
// dispara a animação.
const visible = ref(false);
const sheetOpen = ref(false);

// Popover dos Termos e Condições (o (i) do cabeçalho).
const termsOpen = ref(false);

const code = ref('');
const input = ref<HTMLInputElement | null>(null);

useBodyScrollLock(visible);

// Esta é a ÚNICA folha da casa com input, então é a única que sente o teclado do
// iOS: `fixed` resolve contra o layout viewport, que não encolhe quando o
// teclado sobe, e a folha ficava atrás dele (só se recolocando na segunda vez
// que o teclado abria, quando o `resize` forçava novo layout). Dimensionar o
// overlay pela visual viewport ancora a folha logo acima do teclado e a faz
// acompanhá-lo.
const {
  height: vvHeight,
  width: vvWidth,
  offsetTop: vvTop,
} = useViewportInset();

// Zero = sem a API (SSR, desktop antigo): cai no `inset: 0` do CSS.
const viewportStyle = computed(() =>
  vvHeight.value
    ? {
        '--pc-vv-top': `${vvTop.value}px`,
        '--pc-vv-h': `${vvHeight.value}px`,
      }
    : undefined,
);

/* ---------------- escala do cabeçalho ---------------- */

// Com o teclado aberto sobram ~330px de viewport e a folha inteira pede ~409px.
// Quem cede é o CABEÇALHO, não o formulário: a arte é decoração e os campos são
// a função da tela — encolher o input/botão pra caber a placa seria o troco
// errado. `--pc-art-scale` multiplica as três medidas da arte (largura, sangria
// e o respiro do painel), que são todas % da MESMA régua (a largura da folha),
// então a composição desce inteira e proporcional, sem "emagrecer" o desenho.
//
// A altura da folha em função da escala k é:
//   ART_SPAN% × largura × k  +  FORM_HEIGHT
// onde ART_SPAN = 55,55 (altura da arte) − 36,47 (sangria) + 38,42 (respiro do
// painel) e FORM_HEIGHT é o formulário mais o padding de baixo, que NÃO escalam.
// Invertendo: k = (altura visível − FORM_HEIGHT) / (ART_SPAN% × largura).
const SHEET_MAX_WIDTH = 393;
const ART_SPAN = 0.575;
// 159 do formulário (lead 40 + label 19 + input 44 + botão 56) + 24 de padding
// de baixo + 8 de folga pra borda e safe-area.
const FORM_HEIGHT = 191;
// Abaixo disto a placa fica ilegível — daí em diante quem resolve é o scroll do
// corpo, que continua no lugar como rede de segurança.
const MIN_ART_SCALE = 0.45;

const artScale = computed(() => {
  if (!vvHeight.value || !vvWidth.value) {
    return 1;
  }

  const width = Math.min(SHEET_MAX_WIDTH, vvWidth.value);
  const raw = (vvHeight.value - FORM_HEIGHT) / (ART_SPAN * width);

  // Passo de 0,05: o `offsetTop`/`height` da visual viewport oscilam alguns
  // pixels durante o scroll, e uma escala contínua faria a arte tremer.
  const stepped = Math.floor(Math.min(raw, 1) / 0.05) * 0.05;

  return Math.max(MIN_ART_SCALE, stepped);
});

/* ---------------- modal de aviso ---------------- */

// Estado que NÃO abre o formulário e sim o modal de aviso (BaseNoticeSheet): sem
// input, sem botão — arte de cabeçalho, o motivo e o aviso de nível (o mesmo da
// loja). É o mesmo componente que a roletinha e o check-in sobem.
const isNotice = computed(() => status.value === 'ineligible');

// Os slugs do aviso (degrau mínimo do promocode e nível atual) vêm do
// /players/me, porque a régua é editada no backoffice do core.
const playerProfileStore = usePlayerProfileStore();

const noticeLevelGate = computed(() => ({
  subject: 'Seu promocode',
  minLevel: playerProfileStore.minLevelFor(GAMIFICATION_SLUGS.promocodes),
  currentLevel: playerProfileStore.crmLevel,
}));

// A frase é a MESMA das outras duas features, de propósito: o motivo é o mesmo
// (abaixo do nível mínimo) — o título do aviso de nível.
const noticeMessage = computed(() =>
  levelGateMessage(noticeLevelGate.value.minLevel),
);

// Cabeçalho do aviso: a placa "?" do Figma (file MvX0d5Neu0A19lRCv3fsV4 →
// "Check-in" 310:4148), já na janela do NoticeSheet (277,3 × 218,3 @3x =
// 832×655) — a mesma janela que a arte do check-in usa.
//
// Receita do asset (o `/scripts` é gitignorado, então ela mora aqui):
//  1. `download_assets` do MASK GROUP (310:4169) em png @3x. É ele, e não o
//     "IMAGEM" de dentro nem a arte solta em `rawImages`: o fill tem rotação e
//     recorte próprios, e só o render do grupo traz a placa inclinada como no
//     desenho — reconstruir a partir da arte quadrada dá outro enquadramento.
//  2. O export vem com o cinza da página assado (#1E1E1E opaco, sem alpha).
//     Chave de cor chapada + unmix, por pixel: d = maior |C−30| entre os canais,
//     α = clamp((d−3)/25), A = (C − 30·(1−α))/α. O α proporcional (em vez de um
//     corte seco) é o que preserva o glow laranja como semi-transparente.
//  3. webp qualidade 90.
const noticeArt = computed(() =>
  getAssetsUrlForCDN('/assets/images/promocode/aviso-header.webp'),
);

const asset = (file: string) =>
  getAssetsUrlForCDN(`/assets/images/promocode/${file}`);

/* ---------------- resgate ---------------- */

const canRedeem = computed(
  () => code.value.trim().length > 0 && !isRedeeming.value,
);

async function applyPromocode() {
  const success = await store.redeemPromocode(code.value);

  if (success) {
    code.value = '';
  }
}

function close() {
  store.close();
}

// O painel abre no MESMO passo de render do backdrop (`appear` na Transition),
// como no check-in. O `prefillCode` é consumido aqui e não no watch do input:
// quem abre com código (deep-link `?promocode=XXX`, rota /promocode) manda o
// valor junto do open(), e o modal só o lê ao subir.
watch(
  isOpen,
  (opened) => {
    if (opened) {
      visible.value = true;
      termsOpen.value = false;
      code.value = prefillCode.value;
      sheetOpen.value = true;
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

// Foco DEPOIS do slide-up, não no `nextTick` do open: focar durante a animação
// faz o Safari rodar o scroll-into-view do input enquanto o `transform` ainda
// está em curso, e o documento se desloca sem a folha `fixed` acompanhar.
//
// Sem código pré-preenchido, o teclado já sobe com o cursor no lugar certo. Com
// código, focar seria pedir pra apagar o que veio pronto.
function onOpened() {
  if (!code.value) {
    input.value?.focus();
  }
}

function onClosed() {
  visible.value = false;
  termsOpen.value = false;
  code.value = '';
  store.reset();
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="pc-overlay"
      :style="viewportStyle"
      @dragstart.prevent
    >
      <div
        class="pc-backdrop"
        :class="{ 'pc-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        appear
        name="pc-slide-up"
        @after-enter="onOpened"
        @after-leave="onClosed"
      >
        <!-- Conta que o core ainda não considera ativa: sobe o aviso no lugar do
             formulário — o input e o botão não têm o que fazer nesse estado. -->
        <BaseNoticeSheet
          v-if="sheetOpen && isNotice"
          :accent="ACCENT_COLOR"
          :art="noticeArt"
          :level-gate="noticeLevelGate"
          :message="noticeMessage"
          @close="close"
          @navigate="close"
        />

        <div
          v-else-if="sheetOpen"
          class="pc-frame"
          :style="{ '--pc-art-scale': artScale }"
        >
          <!-- Cabeçalho = arte + os dois botões, os três FORA do painel: a arte
               sangra pra cima dele (que é `overflow: clip`) e os botões precisam
               ficar na frente dela. Esta caixa também é a régua deles: a altura é
               a da arte, então o `top` em % escala com o desenho. Mesmo desenho
               do BaseNoticeSheet — o aviso de elegibilidade é ESTA folha em outro
               estado, não uma tela de outro lugar. -->
          <div class="pc-head">
            <img
              alt=""
              aria-hidden="true"
              class="pc-art"
              :src="noticeArt"
            />

            <!-- Regras de uso à esquerda, espelhando o X: mesmo (i) e mesma
                 posição do Check-in Diário — inclusive o ícone, que é uma cópia
                 do asset dele (cada feature guarda o seu; apontar pra pasta da
                 outra amarraria as duas). Antes era uma pílula no pé do
                 formulário, que competia com o botão de resgate — o lugar de uma
                 leitura opcional é o canto, não a base da ação. -->
            <button
              aria-label="Regras de uso"
              class="pc-icon pc-icon--rules"
              type="button"
              @click="termsOpen = !termsOpen"
            >
              <img
                alt=""
                :src="asset('info.svg')"
              />
            </button>

            <!-- Scrim invisível: fecha o popover ao clicar fora dele (mesmo
                 recurso do (i) do check-in e do dropdown ⋮ da roletinha).
                 `fixed` e não `absolute`: ele vive dentro do cabeçalho, que é
                 só a faixa da arte — preso a ela, não pegaria o clique dado no
                 painel. -->
            <div
              v-if="termsOpen"
              class="pc-scrim"
              @click="termsOpen = false"
            />

            <!-- Popover das regras, ancorado no (i): o mesmo formato do
                 regulamento do check-in — sem botão de confirmar, fecha pelo
                 scrim ou pelo próprio (i). O corpo é a única coisa que rola, pra
                 a barriguinha da caixa não ser cortada (um scroll container clipa
                 o que passa da borda). O TEXTO é o mesmo de sempre
                 (`utils/apostatudo/promocode-terms`), com título e seções. -->
            <Transition name="pc-fade">
              <div
                v-if="termsOpen"
                class="pc-terms"
              >
                <div class="pc-terms__body">
                  <p class="pc-terms__lead">{{ PROMOCODE_TERMS_TITLE }}</p>
                  <p class="pc-terms__text">{{ PROMOCODE_TERMS_INTRO }}</p>

                  <div
                    v-for="section in PROMOCODE_TERMS"
                    :key="section.title"
                    class="pc-terms__section"
                  >
                    <p class="pc-terms__subtitle">{{ section.title }}</p>
                    <p
                      v-for="(paragraph, index) in section.paragraphs"
                      :key="index"
                      class="pc-terms__text"
                    >
                      {{ paragraph }}
                    </p>
                  </div>
                </div>
              </div>
            </Transition>

            <button
              aria-label="Fechar"
              class="pc-icon pc-icon--close"
              type="button"
              @click="close"
            >
              <!-- Inline e não asset: o close.svg de cada feature mora na pasta
                   dela, e apontar pra pasta de outra amarraria as duas (mesma
                   escolha do BaseNoticeSheet). -->
              <svg
                aria-hidden="true"
                fill="none"
                viewBox="0 0 30 30"
              >
                <rect
                  fill="#fff"
                  fill-opacity="0.05"
                  height="30"
                  rx="15"
                  width="30"
                />
                <path
                  d="M19.2784 12.1583L20 11.4399L18.5601 10L17.8417 10.7184L15 13.5601L12.1583 10.7184L11.4399 10L10 11.4399L10.7184 12.1583L13.5601 15L10.7216 17.8417L10 18.5601L11.4399 20L12.1583 19.2816L15 16.4399L17.8417 19.2816L18.5601 20L20 18.5601L19.2816 17.8417L16.4399 15L19.2816 12.1583H19.2784Z"
                  fill="#fff"
                />
              </svg>
            </button>
          </div>

          <div class="pc-panel">
            <!-- Partículas no rés do chão do painel, atrás de tudo (mesma camada
                 da roletinha e do check-in, na cor desta feature). -->
            <BaseParticlesField
              class="pc-particles"
              :color="ACCENT_COLOR"
              travel="100vh"
            />

            <div class="pc-body">
              <p class="pc-lead">Ative o seu código promocional</p>

              <label
                class="pc-label"
                for="pc-input"
                >Código promocional</label
              >

              <input
                id="pc-input"
                ref="input"
                v-model="code"
                autocapitalize="characters"
                autocomplete="off"
                class="pc-input"
                maxlength="40"
                placeholder="Digite seu Promocode"
                spellcheck="false"
                type="text"
                @keyup.enter="applyPromocode"
              />

              <button
                class="pc-submit"
                :class="{ 'pc-submit--off': !canRedeem }"
                :disabled="!canRedeem"
                type="button"
                @click="applyPromocode"
              >
                <NuxtIcon
                  v-if="isRedeeming"
                  class="animate-spin"
                  name="loading"
                  style="width: 12px; height: 12px"
                />
                RESGATAR CÓDIGO
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<!-- Sem scope: @keyframes contornam o reset global de transition (mesma
     estratégia da roletinha, do hub e do check-in). -->
<style>
.pc-slide-up-enter-active {
  animation: pc-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.pc-slide-up-leave-active {
  animation: pc-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes pc-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Abertura do popover das regras: o MESMO fade-descendo do (i) do check-in. */
.pc-fade-enter-active {
  animation: pc-fade 0.16s ease-out forwards;
}
.pc-fade-leave-active {
  animation: pc-fade 0.12s ease-in reverse forwards;
}
@keyframes pc-fade {
  0% {
    transform: translateY(-6px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// Empilha acima do hub Club Fidelidade (z-9990), do CashbackSheet (z-9995) e do
// Check-in (z-9996), abaixo da roletinha (z-9999) — nunca aparecem juntos.
// Altura e topo vêm da VISUAL viewport (`--pc-vv-*`, setadas inline pelo
// script) e não de `inset: 0`: no iOS o teclado encolhe só a visual viewport, e
// um `inset: 0` deixaria a folha ancorada no rodapé do layout viewport, atrás do
// teclado. Os fallbacks (`0` / `100%`) valem pra SSR e pra quem não tem a API —
// aí o comportamento é o antigo, de tela cheia.
.pc-overlay {
  @apply fixed z-[9997] flex flex-col justify-end select-none;

  top: var(--pc-vv-top, 0px);
  left: 0;
  width: 100%;
  height: var(--pc-vv-h, 100%);

  img {
    -webkit-user-drag: none;
  }
}

// Sem `backdrop-filter`, pelo mesmo motivo do check-in: este modal quase sempre
// abre POR CIMA do hub, que já borra a página, e dois blurs de tela cheia
// compostos juntos custam caro justamente no primeiro quadro — o do clique.
// `fixed` e não `absolute`: o overlay agora tem a altura da visual viewport, e
// um backdrop preso a ele deixaria de cobrir a tela toda enquanto o teclado
// estiver aberto (ou durante um scroll com `offsetTop` > 0).
.pc-backdrop {
  @apply fixed inset-0;

  background: rgba(0, 0, 0, 0.72);
  animation: pc-fade-in 0.25s ease forwards;
}

.pc-backdrop--out {
  animation: pc-fade-in 0.24s ease reverse forwards;
}

@keyframes pc-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Bottom-sheet colado no rodapé. Largura no teto de 393px — a MESMA do
// BaseNoticeSheet, e não os 480px das outras folhas: os dois estados desta
// feature (formulário e aviso) sobem no mesmo lugar, e larguras diferentes
// fariam o modal "pular de tamanho" conforme a elegibilidade do jogador.
// CUIDADO com as % daqui pra baixo: elas têm que ficar nos FILHOS da folha,
// nunca nela. O bloco contenedor da folha é o overlay (a tela toda), então uma %
// aqui viraria fração da VIEWPORT — a armadilha documentada no BaseNoticeSheet.
.pc-frame {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: min(393px, 100vw);
  max-height: 100%;
  margin: 0 auto;
}

// Cabeçalho: a arte no fluxo (é o painel que sobe por baixo dela) e os botões
// posicionados contra ESTA caixa, cuja altura é a da arte.
.pc-head {
  @apply relative z-[2] shrink-0;
}

// Janela do Figma (277,3 × 218,3), centrada com o mesmo empurrão de 5,5px pra
// direita (1,407% da folha) do BaseNoticeSheet. A arte já vem recortada nessa
// proporção; `cover` é o cinto de segurança pra uma que chegue com outra.
// A máscara é o que faz o desenho "dissolver" no painel em vez de terminar numa
// aresta reta — prefixada junto porque `mask-image` sem prefixo só vale de iOS
// 15.4 pra cá.
// `--pc-art-scale` (1 por padrão) encolhe a arte quando o teclado come a tela.
// Ela e as duas medidas do painel são % da MESMA régua — a largura da folha —,
// então multiplicar as três pelo mesmo fator desce a composição inteira sem
// distorcer nada.
.pc-art {
  position: relative;
  left: 1.407%;
  display: block;
  width: calc(70.56% * var(--pc-art-scale, 1));
  margin: 0 auto;
  aspect-ratio: 277.291 / 218.318;
  object-fit: cover;
  pointer-events: none;
  -webkit-mask-image: linear-gradient(
    to bottom,
    #000 0,
    #000 62.95%,
    transparent 100%
  );
  mask-image: linear-gradient(to bottom, #000 0, #000 62.95%, transparent 100%);
}

// Os 11px abaixo do topo do painel do Figma, medidos DENTRO do cabeçalho (que é
// só a faixa da arte, logo a régua das % é a altura DELA).
// A conta é `sangria + 11px`, e não os 39,47% de antes: a sangria vale 75 dos
// 218,318px da arte = 34,35%, e essa fração é a mesma em QUALQUER escala, porque
// as duas encolhem juntas. Os 11px ficam fixos porque os botões ficam fixos —
// é o que mantém eles a 11px do topo do painel mesmo com a arte reduzida.
// z acima do scrim, pra o (i) seguir funcionando como toggle com o popover
// aberto (mesma ordem do check-in e do dropdown ⋮ da roletinha).
.pc-icon {
  @apply absolute z-[3];

  top: calc(34.35% + 11px);
  width: 30px;
  height: 30px;

  svg,
  img {
    @apply w-full h-full;
  }
}

.pc-icon--close {
  right: 14px;
}

// Espelha o X, com o (i) do check-in (que já traz o próprio círculo no SVG).
.pc-icon--rules {
  left: 14px;
}

// Captura o clique-fora. `fixed` porque o cabeçalho é só a faixa da arte: preso
// a ela (`absolute`), um clique no painel não fecharia o popover.
.pc-scrim {
  position: fixed;
  z-index: 2;
  inset: 0;
}

// `margin-top` negativo = os 143px que a arte entra no painel (a sangria de 75px
// dela sobra acima); o `padding-top` de 38,42% são os 151px do Figma até o
// conteúdo. z abaixo da arte pro fundo do painel não cobri-la.
.pc-panel {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: calc(-36.47% * var(--pc-art-scale, 1));
  padding: calc(38.42% * var(--pc-art-scale, 1)) 19px
    calc(env(safe-area-inset-bottom, 0px) + 24px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  // Colado na base: só os cantos de cima arredondam e a borda de baixo some.
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  background: linear-gradient(180deg, #00005c 0%, #00001b 100%);
  // `clip` e não `hidden`: `hidden` é scroll container, e o ajuste de foco do
  // clique (aqui tem input, então isso acontece MESMO) deslocaria o painel — o
  // bug das bordas da roletinha.
  overflow: clip;
}

// Partículas no chão do painel e o conteúdo um degrau acima. O conteúdo em fluxo
// precisa de `position: relative` pra participar da ordem de pilha: sem
// posicionamento, uma camada em z-0 pinta por cima dele mesmo vindo antes no DOM.
.pc-particles {
  z-index: 0;
}

/* ---------- formulário ---------- */

// z-1 pra ficar acima das partículas (que estão em z-0 dentro do painel).
// Rola por dentro: com o teclado aberto sobram ~350px de viewport, e o painel é
// `overflow: clip` — sem scroll aqui, o botão de resgate simplesmente
// desapareceria em vez de ficar alcançável.
.pc-body {
  @apply relative z-[1] flex flex-col;

  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.pc-lead {
  @apply text-white font-bold;

  margin-bottom: 16px;
  font-size: 20px;
  line-height: 120%;
}

.pc-label {
  @apply text-[#B4B7BD] font-medium;

  margin-bottom: 8px;
  font-size: 11px;
  line-height: 100%;
}

.pc-input {
  @apply w-full text-[#B0BED9] font-bold;

  height: 44px;
  padding: 0 16px;
  border: 2px solid #161d32;
  border-radius: 4px;
  background: #030d26;
  font-size: 12px;
  line-height: 14px;

  &::placeholder {
    @apply text-[#B0BED9]/60;
  }
}

.pc-submit {
  @apply flex w-full items-center justify-center text-white font-extrabold;

  gap: 7px;
  height: 44px;
  margin-top: 12px;
  border: 1px solid #fc9984;
  border-radius: 4px;
  background: radial-gradient(
    168.3% 138% at 36.76% -16.67%,
    #ff704a 0%,
    #ff3400 100%
  );
  font-size: 13px;
  line-height: 16px;
}

.pc-submit--off {
  @apply text-[#B7B7B7];

  border-color: transparent;
  background: #303747;
}

/* ---------- Termos e Condições ---------- */
// Popover ancorado no (i), com as mesmas medidas e cores do regulamento do
// check-in: 300px de largura, teto de 300px de altura e a barriguinha apontando
// pro botão. Ancorado logo abaixo dele — o (i) começa na sangria da arte (34,35%,
// invariante à escala) mais 11px, e tem 30px, mais 8px de folga.
.pc-terms {
  @apply absolute z-[5] flex flex-col;

  top: calc(34.35% + 49px);
  left: 14px;
  width: min(300px, calc(100% - 28px));
  // 260px e não os 300px do check-in: aqui a caixa nasce 124px abaixo do topo da
  // folha (39,47% da arte + 38px) e a folha do formulário tem ~410px — com 300 a
  // base do popover passava do fim do modal. Quem rola é o corpo.
  // Também limitado pela viewport VISÍVEL: com o teclado aberto a folha inteira
  // tem ~350px, e um teto fixo de 260 estouraria a base dela. Os 140px
  // descontados são o quanto a caixa já nasce abaixo do topo da folha.
  max-height: min(260px, calc(var(--pc-vv-h, 100vh) - 140px));
  padding: 10px;
  border: 1px solid #203570;
  border-radius: 10px;
  background: #0c1b44;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);

  // Barriguinha apontando pro (i): quadradinho girado 45° com só as bordas
  // top/left, centrado sob o botão (que começa em 14px e tem 30px de largura,
  // logo o centro cai em 29px).
  &::before {
    content: '';

    position: absolute;
    top: -6px;
    left: 9px;
    width: 12px;
    height: 12px;
    border-top: 1px solid #203570;
    border-left: 1px solid #203570;
    border-radius: 3px 0 0 0;
    background: #0c1b44;
    transform: rotate(45deg);
  }
}

// O único scroll container da caixa. `min-height: 0` porque item de flex não
// encolhe abaixo do próprio conteúdo sem isso — e sem encolher, nada rola.
.pc-terms__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  // Não encadeia o scroll pro body atrás.
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

// Tipografia na escala do popover do check-in (11px de corpo), e não a de tela
// cheia de antes: a caixa tem 300px e o texto é longo.
.pc-terms__lead {
  @apply text-white font-bold;

  margin-bottom: 6px;
  font-size: 12px;
  line-height: 1.4;
}

.pc-terms__section {
  margin-top: 10px;
}

.pc-terms__subtitle {
  @apply text-white font-bold;

  margin-bottom: 4px;
  font-size: 11px;
  line-height: 1.4;
}

.pc-terms__text {
  @apply text-white/85 font-normal;

  font-size: 11px;
  line-height: 1.4;

  & + & {
    margin-top: 6px;
  }
}

// Desktop: card centrado, como as outras folhas viram card no mesmo ponto.
@media (min-width: 1024px) {
  .pc-overlay {
    @apply justify-center;

    padding: 12px;
  }

  // Largura mantida em 393px (a do BaseNoticeSheet): alargar aqui descolaria os
  // dois estados de novo, e as medidas do cabeçalho são todas % da folha — mexer
  // na largura escala a arte junto.
  .pc-panel {
    padding-bottom: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
  }
}
</style>
